export const REQUIRED_ADDRESS_FIELDS = [
  'firstName',
  'lastName',
  'addressLine1',
  'area',
  'city',
  'state',
  'pincode',
  'phone',
];

export const ORDER_STATUS_LABELS = {
  Placed: 'Order Placed',
  Accepted_By_Farmer: 'Farmer Accepted',
  Packed: 'Packed',
  Ready_To_Dispatch: 'Ready To Dispatch',
  Picked_Up: 'Picked Up',
  Shared_In_Transit: 'Shared In Transit',
  Received_At_Destination: 'Received At Destination',
  Out_For_Delivery: 'Out For Delivery',
  Delivered: 'Delivered',
  Cancelled: 'Cancelled',
};

export const ORDER_PROGRESS_STEPS = [
  'Placed',
  'Accepted_By_Farmer',
  'Ready_To_Dispatch',
  'Picked_Up',
  'Shared_In_Transit',
  'Received_At_Destination',
  'Out_For_Delivery',
  'Delivered',
];

const splitName = (value) => {
  const safeValue = String(value || '').trim();
  if (!safeValue) return { firstName: '', lastName: '' };

  const [firstName = '', ...rest] = safeValue.split(/\s+/);
  return { firstName, lastName: rest.join(' ') };
};

export const normalizeCoordinates = (value) => {
  const source = Array.isArray(value)
    ? value
    : Array.isArray(value?.coordinates)
      ? value.coordinates
      : [];
  const longitude = Number(source[0]);
  const latitude = Number(source[1]);

  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null;
  if (longitude < -180 || longitude > 180 || latitude < -90 || latitude > 90) return null;
  if (longitude === 0 && latitude === 0) return null;
  return [longitude, latitude];
};

export const getCoordinatesFromSource = (source) => {
  if (!source) return null;
  const direct = normalizeCoordinates(source);
  if (direct) return direct;
  if (source.location) return normalizeCoordinates(source.location);
  const longitude = Number(source.longitude ?? source.lng);
  const latitude = Number(source.latitude ?? source.lat);
  return normalizeCoordinates([longitude, latitude]);
};

export const calculateDistanceKmBetweenCoordinates = (from, to) => {
  const fromCoords = normalizeCoordinates(from);
  const toCoords = normalizeCoordinates(to);
  if (!fromCoords || !toCoords) return null;

  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const [fromLng, fromLat] = fromCoords;
  const [toLng, toLat] = toCoords;
  const earthRadiusKm = 6371;
  const latDelta = toRadians(toLat - fromLat);
  const lngDelta = toRadians(toLng - fromLng);
  const a =
    Math.sin(latDelta / 2) ** 2 +
    Math.cos(toRadians(fromLat)) * Math.cos(toRadians(toLat)) * Math.sin(lngDelta / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return roundCurrency(earthRadiusKm * c);
};

export const normalizeAddress = (address = {}, profile = {}) => {
  const derivedName = splitName(profile.name || '');
  const firstName = String(address.firstName ?? derivedName.firstName ?? '').trim();
  const lastName = String(address.lastName ?? derivedName.lastName ?? '').trim();
  const addressLine1 = String(address.addressLine1 ?? address.street ?? '').trim();
  const addressLine2 = String(address.addressLine2 ?? '').trim();
  const area = String(address.area ?? '').trim();
  const city = String(address.city ?? '').trim();
  const state = String(address.state ?? profile.state ?? '').trim();
  const pincode = String(address.pincode ?? address.zipCode ?? '').trim();
  const phone = String(address.phone ?? profile.phone ?? '').trim();
  const alternatePhone = String(address.alternatePhone ?? '').trim();
  const coordinates = normalizeCoordinates(address.coordinates);

  return {
    firstName,
    lastName,
    addressLine1,
    addressLine2,
    area,
    city,
    state,
    pincode,
    phone,
    alternatePhone,
    street: addressLine1,
    zipCode: pincode,
    coordinates,
  };
};

const normalizeRole = (role) => {
  const raw = String(role || '').trim().toLowerCase();
  const aliases = {
    user: 'customer',
    client: 'customer',
    superadmin: 'super_admin',
    'super-admin': 'super_admin',
  };
  return aliases[raw] || raw;
};

export const normalizeUserProfile = (profile = {}) => {
  if (!profile || typeof profile !== 'object') return null;

  return {
    ...profile,
    role: normalizeRole(profile.role),
    phone: String(profile.phone ?? '').trim(),
    state: String(profile.state ?? '').trim(),
    address: normalizeAddress(profile.address, profile),
  };
};

export const mergeAddressDraft = (baseAddress = {}, draftAddress = {}) => {
  const merged = { ...baseAddress };

  Object.entries(draftAddress || {}).forEach(([key, value]) => {
    if (typeof value !== 'string') {
      if (value !== undefined) merged[key] = value;
      return;
    }

    if (value.trim()) {
      merged[key] = value;
    }
  });

  return normalizeAddress(merged, {
    name: [merged.firstName, merged.lastName].filter(Boolean).join(' '),
    phone: merged.phone,
    state: merged.state,
  });
};

export const hasCompleteAddress = (address = {}) =>
  REQUIRED_ADDRESS_FIELDS.every((field) => String(address?.[field] || '').trim());

export const formatAddress = (address = {}) =>
  [
    [address.addressLine1 || address.street, address.addressLine2].filter(Boolean).join(', '),
    [address.area, address.city].filter(Boolean).join(', '),
    [address.state, address.pincode || address.zipCode].filter(Boolean).join(' - '),
  ]
    .filter(Boolean)
    .join(', ');

export const buildGoogleMapsDirectionsUrl = (address = {}) => {
  const coords = Array.isArray(address?.coordinates) ? address.coordinates : [];
  const latitude = Number(coords[1]);
  const longitude = Number(coords[0]);

  if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${latitude},${longitude}`)}&travelmode=driving`;
  }

  const destination = formatAddress(address);
  if (!destination) return '';
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}&travelmode=driving`;
};

export const getStatusLabel = (status) => ORDER_STATUS_LABELS[status] || status || 'Unknown';

export const getTrackingMilestones = (order) => {
  const activeSteps =
    order?.deliveryMode === 'shared'
      ? ORDER_PROGRESS_STEPS
      : ORDER_PROGRESS_STEPS.filter((step) => !['Shared_In_Transit', 'Received_At_Destination'].includes(step));

  const currentIndex = activeSteps.indexOf(order?.orderStatus);
  return activeSteps.map((status, index) => ({
    status,
    label: getStatusLabel(status),
    done: currentIndex >= index,
    active: currentIndex === index,
  }));
};

export const roundCurrency = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return 0;
  return Math.round((numeric + Number.EPSILON) * 100) / 100;
};

const clampDiscount = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return 0;
  return Math.min(95, Math.max(0, numeric));
};

const resolveProductId = (product = {}) => product._id || product.id || '';

const resolveDealProducts = (deal = {}) => {
  if (Array.isArray(deal.products)) return deal.products;
  if (Array.isArray(deal.productIds)) return deal.productIds;
  return [];
};

export const getDiscountedUnitPrice = (basePrice, discountPercent = 0) =>
  roundCurrency(Number(basePrice || 0) * (1 - clampDiscount(discountPercent) / 100));

export const mapProductToCheckoutItem = (product, quantity = 1, overrides = {}) => {
  const productId = resolveProductId(product);
  const farmerRef = product?.farmerId && typeof product.farmerId === 'object' ? product.farmerId : {};
  const farmerLocation = getCoordinatesFromSource(farmerRef) || getCoordinatesFromSource(product?.farmerLocation);
  const farmerArea = product?.farmerArea || farmerRef.village || farmerRef.mandal || '';
  const farmerCity = product?.farmerCity || farmerRef.city || farmerRef.mandal || farmerRef.village || '';

  return {
    id: productId,
    cartKey: `product:${productId}`,
    itemType: 'product',
    name: product.name,
    image: product.images?.[0] || product.image || '',
    price: roundCurrency(product.pricePerKg ?? product.price ?? 0),
    farmerId: product.farmerId?._id || product.farmerId,
    farmerName: farmerRef.name || product?.farmerName || '',
    farmerLocation,
    farmerAddress: product?.farmerAddress || farmerRef.address || farmerRef.addressLine1 || '',
    farmerAddressLine1: product?.farmerAddressLine1 || farmerRef.addressLine1 || farmerRef.address || '',
    farmerAddressLine2: product?.farmerAddressLine2 || farmerRef.addressLine2 || '',
    farmerArea,
    farmerCity,
    farmerDistrict: product?.farmerDistrict || farmerRef.district || '',
    farmerState: product?.farmerState || farmerRef.state || '',
    farmerPincode: product?.farmerPincode || farmerRef.pincode || '',
    unitPriceLabel: '/kg',
    quantityLabel: 'kg',
    quantity: Number(quantity || product.quantity || 1),
    ...overrides,
  };
};

const normalizeEquipmentCategory = (value) => String(value || '').trim().toLowerCase();
const isToolsEquipmentCategory = (value) => normalizeEquipmentCategory(value) === 'tools';

export const buildDealCheckoutItems = (deal, quantityPerProduct = 1) => {
  const dealId = String(deal?._id || deal?.id || '').trim();
  const dealName = String(deal?.dealName || deal?.name || '').trim();
  const dealOccasion = String(deal?.occasion || '').trim();
  const dealDiscountPercent = clampDiscount(deal?.discountPercent);

  return resolveDealProducts(deal)
    .filter((product) => resolveProductId(product))
    .map((product) => {
      const productId = resolveProductId(product);
      const originalUnitPrice = roundCurrency(product.pricePerKg ?? product.price ?? 0);
      const appliedUnitPrice = getDiscountedUnitPrice(originalUnitPrice, dealDiscountPercent);

      return mapProductToCheckoutItem(product, quantityPerProduct, {
        cartKey: `deal:${dealId}:product:${productId}`,
        price: appliedUnitPrice,
        originalUnitPrice,
        appliedUnitPrice,
        dealId,
        dealName,
        dealOccasion,
        dealDiscountPercent,
      });
    });
};

export const summarizeDealPricing = (deal) => {
  const items = buildDealCheckoutItems(deal, 1);
  const originalTotal = roundCurrency(
    items.reduce((sum, item) => sum + Number(item.originalUnitPrice || item.price || 0), 0)
  );
  const discountedTotal = roundCurrency(
    items.reduce((sum, item) => sum + Number(item.appliedUnitPrice || item.price || 0), 0)
  );

  return {
    items,
    originalTotal,
    discountedTotal,
    savings: roundCurrency(Math.max(0, originalTotal - discountedTotal)),
  };
};

export const buildDealPackageCheckoutItem = (deal, packageQuantity = 1) => {
  const dealId = String(deal?._id || deal?.id || '').trim();
  const dealName = String(deal?.dealName || deal?.name || 'Farm Package').trim();
  const dealOccasion = String(deal?.occasion || '').trim();
  const dealDiscountPercent = clampDiscount(deal?.discountPercent);
  const products = resolveDealProducts(deal);
  const pricing = summarizeDealPricing(deal);

  return {
    id: dealId || dealName,
    cartKey: `package:${dealId || dealName}`,
    itemType: 'package',
    name: dealName,
    image: products[0]?.images?.[0] || products[0]?.image || '',
    price: pricing.discountedTotal,
    originalPrice: pricing.originalTotal,
    packageSavings: pricing.savings,
    dealId,
    dealName,
    dealOccasion,
    dealDiscountPercent,
    itemCount: pricing.items.length,
    packageItems: pricing.items,
    unitPriceLabel: '/pack',
    quantityLabel: 'packs',
    quantity: Number(packageQuantity || 1),
  };
};

export const mapEquipmentToCheckoutItem = (equipment, quantityDays = 1) => ({
  id: equipment._id || equipment.id,
  cartKey: `equipment:${equipment._id || equipment.id}`,
  itemType: 'equipment',
  name: equipment.name,
  image: equipment.images?.[0] || equipment.image || '',
  category: equipment.category || 'Other',
  price: Number(equipment.pricePerDay ?? equipment.price ?? 0),
  deliveryCharge: roundCurrency(equipment.deliveryCharge ?? 0),
  dealerId: equipment.dealerId?._id || equipment.dealerId || '',
  equipmentDealId: equipment._id || equipment.id,
  dealerAddress: equipment.dealerId?.address || '',
  dealerCity: equipment.dealerId?.cityLocation || '',
  dealerDistrict: equipment.dealerId?.district || '',
  dealerState: equipment.dealerId?.state || '',
  dealerPincode: equipment.dealerId?.pincode || '',
  unitPriceLabel: '/day',
  quantityLabel: 'days',
  quantity: Number(quantityDays || equipment.quantity || 1),
});

export const DEFAULT_CHECKOUT_FEE_SETTINGS = {
  protectPromiseFee: 2,
  defaultDeliveryFee: 7,
  defaultDeliveryDistanceKm: 5,
  heavyDeliveryRule: {
    enabled: false,
    weightThresholdKg: 15,
    rateRules: [
      { location: 'All locations', operator: '>=', km: 0, ratePerKm: 0, enabled: true },
    ],
  },
  convenienceFees: [
    { paymentMethod: 'card', label: 'CreditDebit Card convenience fee', type: 'percentage', value: 2, enabled: true },
    { paymentMethod: 'upi', label: 'UPI convenience fee', type: 'percentage', value: 0, enabled: true },
    { paymentMethod: 'emi', label: 'EMI processing fee', type: 'percentage', value: 1, enabled: true },
    { paymentMethod: 'other', label: 'Other payment fee', type: 'flat', value: 0, enabled: false },
  ],
  deliveryRules: [
    { label: 'Local delivery', operator: '<=', km: 5, amount: 7, enabled: true },
    { label: 'Extended delivery', operator: '>', km: 5, amount: 20, enabled: true },
  ],
};

const normalizeFeeNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return fallback;
  return roundCurrency(numeric);
};

const isDistanceBasedCheckoutItem = (item = {}) => {
  if (item?.itemType === 'equipment') return isToolsEquipmentCategory(item?.category);
  return true;
};

const getFixedEquipmentDeliveryCharge = (item = {}) =>
  item?.itemType === 'equipment' ? normalizeFeeNumber(item.deliveryCharge, 0) : 0;

export const normalizeCheckoutFeeSettings = (settings = {}) => {
  const source = settings && typeof settings === 'object' ? settings : {};
  const convenienceFees = Array.isArray(source.convenienceFees) && source.convenienceFees.length
    ? source.convenienceFees
    : DEFAULT_CHECKOUT_FEE_SETTINGS.convenienceFees;
  const deliveryRules = Array.isArray(source.deliveryRules) && source.deliveryRules.length
    ? source.deliveryRules
    : DEFAULT_CHECKOUT_FEE_SETTINGS.deliveryRules;
  const rawHeavyRule = source?.heavyDeliveryRule || {};
  const heavyRateRules = Array.isArray(rawHeavyRule.rateRules) && rawHeavyRule.rateRules.length
    ? rawHeavyRule.rateRules
    : (rawHeavyRule.ratePerKgPerKm !== undefined
      ? [{ location: 'All locations', operator: '>=', km: 0, ratePerKm: rawHeavyRule.ratePerKgPerKm, enabled: true }]
      : DEFAULT_CHECKOUT_FEE_SETTINGS.heavyDeliveryRule.rateRules);

  return {
    protectPromiseFee: normalizeFeeNumber(source.protectPromiseFee, DEFAULT_CHECKOUT_FEE_SETTINGS.protectPromiseFee),
    defaultDeliveryFee: normalizeFeeNumber(source.defaultDeliveryFee, DEFAULT_CHECKOUT_FEE_SETTINGS.defaultDeliveryFee),
    defaultDeliveryDistanceKm: normalizeFeeNumber(
      source.defaultDeliveryDistanceKm,
      DEFAULT_CHECKOUT_FEE_SETTINGS.defaultDeliveryDistanceKm
    ),
    heavyDeliveryRule: {
      enabled: rawHeavyRule.enabled === true || rawHeavyRule.enabled === 'true' || rawHeavyRule.enabled === 1 || rawHeavyRule.enabled === '1',
      weightThresholdKg: normalizeFeeNumber(
        rawHeavyRule.weightThresholdKg,
        DEFAULT_CHECKOUT_FEE_SETTINGS.heavyDeliveryRule.weightThresholdKg
      ),
      rateRules: heavyRateRules.map((rule) => ({
        location: String(rule?.location || 'All locations').trim() || 'All locations',
        operator: ['<', '<=', '=', '>=', '>'].includes(rule?.operator) ? rule.operator : '<=',
        km: normalizeFeeNumber(rule?.km, 0),
        ratePerKm: normalizeFeeNumber(rule?.ratePerKm, 0),
        enabled: rule?.enabled !== false && rule?.enabled !== 'false' && rule?.enabled !== 0 && rule?.enabled !== '0',
      })),
    },
    convenienceFees: convenienceFees.map((fee) => ({
      paymentMethod: String(fee.paymentMethod || 'other').trim().toLowerCase(),
      label: String(fee.label || 'Convenience fee').trim(),
      type: fee.type === 'flat' ? 'flat' : 'percentage',
      value: normalizeFeeNumber(fee.value, 0),
      enabled: fee.enabled !== false,
    })),
    deliveryRules: deliveryRules.map((rule) => ({
      label: String(rule.label || 'Delivery fee').trim(),
      operator: ['<', '<=', '=', '>=', '>'].includes(rule.operator) ? rule.operator : '<=',
      km: normalizeFeeNumber(rule.km, 0),
      amount: normalizeFeeNumber(rule.amount, 0),
      enabled: rule.enabled !== false,
    })),
  };
};

const normalizePaymentMethod = (method) => {
  const normalized = String(method || 'other').trim().toLowerCase();
  if (normalized === 'saved') return 'card';
  if (normalized === 'credit_card' || normalized === 'debit_card') return 'card';
  return normalized || 'other';
};

export const matchesDeliveryRule = (distanceKm, rule = {}) => {
  const distance = Number(distanceKm);
  const target = Number(rule.km);
  if (!Number.isFinite(distance) || !Number.isFinite(target)) return false;

  switch (rule.operator) {
    case '<':
      return distance < target;
    case '<=':
      return distance <= target;
    case '=':
      return distance === target;
    case '>=':
      return distance >= target;
    case '>':
      return distance > target;
    default:
      return false;
  }
};

const sortDeliveryRulesByCloseness = (distanceKm, rules = []) =>
  [...rules].sort((left, right) => {
    const leftGap = Math.abs(Number(distanceKm) - Number(left?.km || 0));
    const rightGap = Math.abs(Number(distanceKm) - Number(right?.km || 0));
    if (leftGap !== rightGap) return leftGap - rightGap;
    return Number(left?.km || 0) - Number(right?.km || 0);
  });

export const resolveClosestDeliveryRule = (distanceKm, rules = []) => {
  const distance = Number(distanceKm);
  if (!Number.isFinite(distance)) return null;

  const activeRules = (Array.isArray(rules) ? rules : [])
    .filter((rule) => rule?.enabled && Number.isFinite(Number(rule?.km)));
  if (!activeRules.length) return null;

  const matchingRules = activeRules.filter((rule) => matchesDeliveryRule(distance, rule));
  const candidateRules = matchingRules.length ? matchingRules : activeRules;
  return sortDeliveryRulesByCloseness(distance, candidateRules)[0] || null;
};

const normalizeServiceLocation = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ');

const isGlobalServiceLocation = (location) =>
  ['all', 'all locations', 'default', 'any'].includes(normalizeServiceLocation(location));

const getDeliveryAddressLocations = (address = {}) => {
  const source = address && typeof address === 'object' ? address : {};
  return [
    source.area,
    source.city,
    source.district,
    source.state,
    source.pincode,
    source.zipCode,
    source.postalCode,
  ].map(normalizeServiceLocation).filter(Boolean);
};

const resolveHeavyDeliveryRateRule = (distanceKm, rules = [], deliveryAddress = {}) => {
  const activeRules = (Array.isArray(rules) ? rules : []).filter((rule) => rule?.enabled);
  const locations = getDeliveryAddressLocations(deliveryAddress);
  const localRules = activeRules.filter((rule) => {
    const location = normalizeServiceLocation(rule?.location);
    return location && !isGlobalServiceLocation(location) && locations.includes(location);
  });
  const globalRules = activeRules.filter((rule) => isGlobalServiceLocation(rule?.location));
  return resolveClosestDeliveryRule(distanceKm, localRules.length ? localRules : globalRules);
};

export const calculateCheckoutFees = ({
  subtotal = 0,
  paymentMethod = 'other',
  distanceKm,
  settings,
  includeConvenience = true,
  items = [],
  deliveryAddress = {},
} = {}) => {
  const feeSettings = normalizeCheckoutFeeSettings(settings);
  const activeSubtotal = normalizeFeeNumber(subtotal, 0);
  const rawDistanceKm = typeof distanceKm === 'string' && String(distanceKm).trim() !== ''
    ? Number(distanceKm)
    : distanceKm;
  const hasDistanceQuote = Number.isFinite(rawDistanceKm);
  const activeDistanceKm = hasDistanceQuote
    ? normalizeFeeNumber(rawDistanceKm, feeSettings.defaultDeliveryDistanceKm)
    : null;
  const normalizedItems = Array.isArray(items) ? items : [];
  const weightItems = normalizedItems.flatMap((item) => {
    if (item?.itemType !== 'package') return [item];
    const packageQuantity = normalizeFeeNumber(item?.quantity, 1);
    return (Array.isArray(item?.packageItems) ? item.packageItems : []).map((child) => ({
      ...child,
      quantity: normalizeFeeNumber(child?.quantity, 1) * packageQuantity,
    }));
  });
  const netWeightKg = roundCurrency(
    weightItems
      .filter((item) => item?.itemType !== 'equipment')
      .reduce((totalWeight, item) => totalWeight + normalizeFeeNumber(item?.quantity, 0), 0)
  );
  const distanceBasedItemCount = normalizedItems.length
    ? normalizedItems.filter(isDistanceBasedCheckoutItem).length
    : (activeSubtotal > 0 ? 1 : 0);
  const fixedEquipmentDeliveryFee = roundCurrency(
    normalizedItems.reduce((sum, item) => sum + getFixedEquipmentDeliveryCharge(item), 0)
  );
  const matchingDeliveryRule = hasDistanceQuote
    ? resolveClosestDeliveryRule(activeDistanceKm, feeSettings.deliveryRules)
    : null;
  const effectivePaymentMethod = normalizePaymentMethod(paymentMethod);
  const matchingConvenienceFee =
    feeSettings.convenienceFees.find((fee) => fee.enabled && fee.paymentMethod === effectivePaymentMethod) ||
    feeSettings.convenienceFees.find((fee) => fee.enabled && fee.paymentMethod === 'other');

  const protectFee = activeSubtotal > 0 ? feeSettings.protectPromiseFee : 0;
  const distanceDeliveryFee = activeSubtotal > 0 && distanceBasedItemCount > 0 && hasDistanceQuote
    ? roundCurrency(matchingDeliveryRule?.amount ?? feeSettings.defaultDeliveryFee)
    : 0;
  const heavyDeliveryRule = feeSettings.heavyDeliveryRule;
  const exceedsHeavyDeliveryThreshold = netWeightKg > heavyDeliveryRule.weightThresholdKg;
  const matchingHeavyDeliveryRateRule = heavyDeliveryRule.enabled && hasDistanceQuote
    ? resolveHeavyDeliveryRateRule(activeDistanceKm, heavyDeliveryRule.rateRules, deliveryAddress)
    : null;
  const heavyDeliveryFee = activeSubtotal > 0 && hasDistanceQuote && exceedsHeavyDeliveryThreshold && matchingHeavyDeliveryRateRule
    ? roundCurrency(matchingHeavyDeliveryRateRule.ratePerKm * activeDistanceKm)
    : 0;
  const deliveryFee = roundCurrency(distanceDeliveryFee + heavyDeliveryFee + fixedEquipmentDeliveryFee);
  const convenienceBaseAmount = roundCurrency(activeSubtotal + protectFee + deliveryFee);
  const convenienceFee = includeConvenience && activeSubtotal > 0 && matchingConvenienceFee
    ? roundCurrency(
        matchingConvenienceFee.type === 'percentage'
          ? (convenienceBaseAmount * Number(matchingConvenienceFee.value || 0)) / 100
          : matchingConvenienceFee.value
      )
    : 0;

  return {
    protectFee,
    distanceDeliveryFee,
    heavyDeliveryFee,
    heavyDeliveryWeightKg: netWeightKg,
    heavyDeliveryThresholdKg: heavyDeliveryRule.weightThresholdKg,
    heavyDeliveryRatePerKm: matchingHeavyDeliveryRateRule?.ratePerKm ?? 0,
    heavyDeliveryRateLocation: matchingHeavyDeliveryRateRule?.location || '',
    heavyDeliveryRateRule: matchingHeavyDeliveryRateRule || null,
    fixedEquipmentDeliveryFee,
    deliveryFee,
    convenienceFee,
    totalFees: roundCurrency(protectFee + deliveryFee + convenienceFee),
    total: roundCurrency(activeSubtotal + protectFee + deliveryFee + convenienceFee),
    deliveryDistanceKm: activeDistanceKm ?? 0,
    hasDistanceQuote,
    deliveryRule: matchingDeliveryRule || null,
    convenienceFeeRule: matchingConvenienceFee || null,
    convenienceFeeLabel: matchingConvenienceFee?.label || 'Convenience fee',
  };
};

const normalizeLocationText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ');

const extractPincode = (source = {}) => {
  const candidates = [
    source.pincode,
    source.zipCode,
    source.postalCode,
    source.pinCode,
    source.address,
    source.addressLine1,
    source.addressLine2,
    source.street,
  ];
  return candidates
    .map((value) => String(value || '').match(/\b\d{6}\b/)?.[0])
    .find(Boolean) || '';
};

const getLocationTokens = (source = {}) => ({
  pincode: extractPincode(source),
  state: normalizeLocationText(source.state),
  district: normalizeLocationText(source.district || source.city),
  city: normalizeLocationText(source.city || source.district),
  area: normalizeLocationText(source.area),
  address: normalizeLocationText(
    [source.address, source.addressLine1 || source.street, source.addressLine2].filter(Boolean).join(' ')
  ),
});

const buildZomatoStyleCoordinateEstimate = (from = {}, to = {}) => {
  const directDistanceKm = calculateDistanceKmBetweenCoordinates(
    getCoordinatesFromSource(from),
    getCoordinatesFromSource(to)
  );
  if (!Number.isFinite(directDistanceKm)) return null;

  return {
    distanceKm: roundCurrency(Math.max(1, directDistanceKm * 1.3)),
    method: 'zomato_geo_estimate',
    label: 'Estimated from pickup and delivery coordinates',
  };
};

const estimateZomatoStyleDistanceFromAreaLocation = (from = {}, to = {}) => {
  const fromLocation = getLocationTokens(from);
  const toLocation = getLocationTokens(to);

  if (fromLocation.area && fromLocation.area === toLocation.area) {
    return { distanceKm: 4, method: 'zomato_same_area', label: 'Estimated from matching area or locality' };
  }

  if (fromLocation.pincode && fromLocation.pincode === toLocation.pincode) {
    return { distanceKm: 18, method: 'zomato_same_pincode', label: 'Estimated from matching pincode with different localities' };
  }

  return null;
};

const estimateZomatoStyleDistanceFromCity = (from = {}, to = {}) => {
  const fromLocation = getLocationTokens(from);
  const toLocation = getLocationTokens(to);
  if (fromLocation.city && toLocation.city && fromLocation.city === toLocation.city) {
    return { distanceKm: 20, method: 'zomato_same_city', label: 'Estimated from matching city' };
  }
  if (fromLocation.district && toLocation.district && fromLocation.district === toLocation.district) {
    return { distanceKm: 28, method: 'zomato_same_district', label: 'Estimated from the same district' };
  }
  if (fromLocation.state && toLocation.state && fromLocation.state === toLocation.state) {
    return { distanceKm: 35, method: 'zomato_same_state', label: 'Estimated from the same state' };
  }
  if (fromLocation.state && toLocation.state) {
    return { distanceKm: 75, method: 'zomato_cross_state', label: 'Estimated from a different state' };
  }
  return null;
};

const shouldPreferTextualEstimate = (coordinateEstimate, textEstimate) => {
  if (!Number.isFinite(coordinateEstimate?.distanceKm) || !Number.isFinite(textEstimate?.distanceKm)) return false;

  const coordinateKm = Number(coordinateEstimate.distanceKm);
  const textualKm = Number(textEstimate.distanceKm);
  if (textualKm < 10) return false;

  return coordinateKm <= 6 || (coordinateKm < textualKm * 0.55 && textualKm - coordinateKm >= 6);
};

const estimateDistanceFromAddress = (from = {}, to = {}) => {
  const coordinateEstimate = buildZomatoStyleCoordinateEstimate(from, to);
  const areaEstimate = estimateZomatoStyleDistanceFromAreaLocation(from, to);
  const cityEstimate = estimateZomatoStyleDistanceFromCity(from, to);
  const textEstimate = areaEstimate || cityEstimate;

  if (coordinateEstimate && !shouldPreferTextualEstimate(coordinateEstimate, textEstimate)) {
    return coordinateEstimate;
  }

  return textEstimate || coordinateEstimate;
};

const expandItemsForDistance = (items = []) =>
  items.flatMap((item) => {
    if (item?.itemType !== 'package') return [item];
    return Array.isArray(item.packageItems) && item.packageItems.length ? item.packageItems : [item];
  });

const getProductLookupValue = (productLookup, item) => {
  if (!productLookup || !item?.id) return null;
  if (productLookup instanceof Map) return productLookup.get(String(item.id));
  return productLookup[String(item.id)] || null;
};

export const resolveCheckoutDeliveryDistance = ({
  items = [],
  customerAddress,
  productLookup,
  fallbackDistanceKm = DEFAULT_CHECKOUT_FEE_SETTINGS.defaultDeliveryDistanceKm,
} = {}) => {
  const fallbackDistance = normalizeFeeNumber(fallbackDistanceKm, DEFAULT_CHECKOUT_FEE_SETTINGS.defaultDeliveryDistanceKm);
  const customerLocation = getLocationTokens(customerAddress || {});
  const distanceItems = expandItemsForDistance(items).filter(isDistanceBasedCheckoutItem);

  const distances = distanceItems
    .map((item) => {
      if (item?.itemType === 'equipment') {
        const dealerSource = {
          address: item?.dealerAddress || '',
          addressLine1: item?.dealerAddress || '',
          area: item?.dealerCity || '',
          city: item?.dealerCity || item?.dealerDistrict || '',
          pincode: item?.dealerPincode || '',
          district: item?.dealerDistrict || '',
          state: item?.dealerState || '',
        };
        const bestEstimate = estimateDistanceFromAddress(dealerSource, customerAddress || {});
        const distanceKm = bestEstimate?.distanceKm;
        if (!Number.isFinite(distanceKm)) return null;

        return {
          itemId: item.id,
          itemName: item.name || 'Equipment',
          farmerName: 'Dealer',
          distanceKm,
          method: bestEstimate?.method || '',
          label: bestEstimate?.label || '',
          isEstimated: true,
        };
      }

      const product = getProductLookupValue(productLookup, item);
      const farmerRef = product?.farmerId && typeof product.farmerId === 'object' ? product.farmerId : {};
      const farmerSource = {
        address: item?.farmerAddress || product?.farmerAddress || farmerRef.address || '',
        addressLine1: item?.farmerAddressLine1 || product?.farmerAddressLine1 || farmerRef.addressLine1 || farmerRef.address || '',
        addressLine2: item?.farmerAddressLine2 || product?.farmerAddressLine2 || farmerRef.addressLine2 || '',
        area: item?.farmerArea || product?.farmerArea || farmerRef.village || farmerRef.mandal || '',
        city: item?.farmerCity || product?.farmerCity || farmerRef.city || farmerRef.mandal || farmerRef.village || '',
        pincode: item?.farmerPincode || product?.farmerPincode || farmerRef.pincode || '',
        district: item?.farmerDistrict || product?.farmerDistrict || farmerRef.district || '',
        state: item?.farmerState || product?.farmerState || farmerRef.state || '',
      };
      const bestEstimate = estimateDistanceFromAddress(farmerSource, customerAddress || {});
      const distanceKm = bestEstimate?.distanceKm;
      if (!Number.isFinite(distanceKm)) return null;

      return {
        itemId: item.id,
        itemName: item.name || product?.name || 'Item',
        farmerName: item.farmerName || farmerRef.name || product?.farmerName || 'Farmer',
        distanceKm,
        method: bestEstimate?.method || '',
        label: bestEstimate?.label || '',
        isEstimated: true,
      };
    })
    .filter(Boolean);

  if (!distanceItems.length) {
    return {
      distanceKm: 0,
      isCalculated: false,
      isEstimated: false,
      method: 'not_required',
      reason: 'No distance-based delivery items in this checkout.',
      distances: [],
    };
  }

  if (!distances.length) {
    const hasCustomerFallbackAddress = Boolean(
      customerLocation.pincode || customerLocation.city || customerLocation.district || customerLocation.state
    );

    return {
      distanceKm: fallbackDistance,
      isCalculated: false,
      isEstimated: false,
      method: 'default_distance',
      reason: hasCustomerFallbackAddress
        ? 'Pickup source address or pincode is missing.'
        : 'Customer address or pincode is missing.',
      distances: [],
    };
  }

  const farthest = distances.reduce((max, item) => (item.distanceKm > max.distanceKm ? item : max), distances[0]);

  return {
    distanceKm: farthest.distanceKm,
    isCalculated: true,
    isEstimated: Boolean(farthest.isEstimated),
    method: farthest.method || 'gps',
    label: farthest.label || '',
    reason: '',
    farthest,
    distances,
  };
};

export const getDeliveryAgentTravelSummary = (order = {}) => {
  const deliveryAgent = order?.deliveryAgentId && typeof order.deliveryAgentId === 'object'
    ? order.deliveryAgentId
    : null;
  const dutyLocation = deliveryAgent?.currentDutyLocation || null;
  if (!deliveryAgent || !dutyLocation) {
    return {
      distanceKm: null,
      etaMinutes: null,
      isEstimated: false,
      label: '',
      lastUpdatedAt: null,
      onDuty: false,
      locationLabel: '',
    };
  }

  const customerAddress = order?.deliveryAddress || {};
  const gpsDistanceKm = calculateDistanceKmBetweenCoordinates(
    dutyLocation?.coordinates,
    customerAddress?.coordinates
  );
  const addressEstimate = gpsDistanceKm === null
    ? estimateDistanceFromAddress(dutyLocation, customerAddress)
    : null;
  const distanceKm = Number.isFinite(gpsDistanceKm)
    ? gpsDistanceKm
    : Number.isFinite(addressEstimate?.distanceKm)
      ? addressEstimate.distanceKm
      : null;

  return {
    distanceKm,
    etaMinutes: Number.isFinite(distanceKm) ? Math.max(8, Math.round((distanceKm / 24) * 60)) : null,
    isEstimated: gpsDistanceKm === null && Number.isFinite(addressEstimate?.distanceKm),
    label: Number.isFinite(gpsDistanceKm) ? 'Live GPS distance from delivery agent' : addressEstimate?.label || '',
    lastUpdatedAt: dutyLocation?.updatedAt || null,
    onDuty: Boolean(deliveryAgent?.isOnDuty),
    locationLabel: [
      dutyLocation?.label,
      dutyLocation?.area,
      dutyLocation?.city,
      dutyLocation?.district,
      dutyLocation?.state,
    ].filter(Boolean).join(', '),
  };
};

export const getDeliveryDecisionRemainingMs = (order) => {
  const unlockAt = order?.agentDecisionAvailableAt ? new Date(order.agentDecisionAvailableAt).getTime() : 0;
  return Math.max(0, unlockAt - Date.now());
};

export const formatDuration = (ms) => {
  if (!ms) return '0m';
  const totalMinutes = Math.ceil(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
};
